import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { getEnumFromValueType } from '@semios/app-platform-values-helpers-and-types'
import { makeCsvString } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/makeCsvString'
import { mapCsvTypeToAvailableFieldAssetLabels } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/mapCsvTypeToAvailableFieldAssetLabels'
import { mapCsvTypeToFieldAssetNameColumnHeader } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/mapCsvTypeToFieldAssetNameColumnHeader'
import { mapCsvTypeToFieldAssetTypeFilename } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/mapCsvTypeToFieldAssetTypeFilename'
import { mapCsvTypeToRenderer } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/mapCsvTypeToRenderer'
import { populateHeaderAndRowMaps } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/populateHeaderAndRowMaps'
import { sanitizeFilename } from 'App/data-export/components/_utils/createAndDownloadCsvIfApplicable/_utils/sanitizeFilename'
import type { TDataExportStoreState } from 'App/data-export/store/dataExportStore'
import { mapAggregationToLabel } from 'App/data-export/store/selectors/_utils/mapAggregationToLabel'
import type { TDataExportValueTypesTimeseriesAggregation } from 'App/data-export/types/from-code-gen/TDataExportValueTypesTimeseriesAggregation'
import type { TCsvHeaderMap, TCsvRowMap } from 'App/data-export/types/types'
import { translate } from 'i18n/i18n'
import type { TFieldAssetStoreState } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { isSemiosEmployeeOrTester } from 'utils/isSemiosEmployeeOrTester'

export type TCsvType = 'blocks' | 'irrigationZones' | 'points'

export const createCsv = (args: {
  data: routes.Values.Response
  property: TProperty
  type: TCsvType
  dataExportStoreState: TDataExportStoreState
  fieldAssetStoreState: TFieldAssetStoreState
}): false | { filename: string; content: string } => {
  const csvHeaderMap: TCsvHeaderMap = new Map([
    ['fieldAssetItem.propertyName', translate.phrases.banyanApp('Property')],
    ['fieldAssetItem.name', mapCsvTypeToFieldAssetNameColumnHeader[args.type]()],
    ['timestamp', translate.phrases.banyanApp('Date')],
  ])

  if (args.type === 'blocks') {
    csvHeaderMap.set('cropName', translate.phrases.banyanApp('Crop'))
  }

  const csvRowMap: TCsvRowMap = new Map()
  const assetValues: routes.Values.Response[typeof args.type] = args.data?.[args.type] ?? {}

  const fieldAssetLabels = mapCsvTypeToAvailableFieldAssetLabels[args.type](
    args.dataExportStoreState,
    args.fieldAssetStoreState,
  )

  const interval = args.dataExportStoreState.formState.interval as TDataExportValueTypesTimeseriesAggregation
  const dateFormat = interval === 'DAILY' ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ssZ'

  Object.entries(assetValues).forEach(([fieldAssetId, { values }]) => {
    Object.entries(values ?? {}).forEach(([valueType, items]) => {
      // TODO: Doing the includes here as a hacky way to deal with overfetching bugs... e.g. daily block values for evapotranspiration always return Eto, Etc and Kc even if you only request one of those values...
      const renderer = args.dataExportStoreState.formState.valueTypes.includes(valueType)
        ? mapCsvTypeToRenderer[args.type](valueType)
        : undefined

      if (renderer) {
        const fieldAssetKey =
          args.type === 'irrigationZones'
            ? `${fieldAssetId}|||${getEnumFromValueType(valueType)}`
            : fieldAssetId

        populateHeaderAndRowMaps({
          csvRowMap,
          csvHeaderMap,
          csvType: args.type,
          dateFormat,
          fieldAssetItem: {
            id: fieldAssetKey,
            name: fieldAssetLabels[fieldAssetKey],
            propertyId: String(args.property.propertyId),
            propertyName: args.property.propertyName,
            propertyTimezone: args.property.timezone,
          },
          // @ts-ignore
          items: items ?? [],
          // @ts-ignore
          renderValuesAndLabels: renderer,
          vt: valueType,
        })
      }
    })
  })

  if (csvRowMap.size > 0) {
    const textSemios = translate.phrases.banyanApp('Semios')
    const textInterval = mapAggregationToLabel[interval]()
    const textValues = translate.phrases.banyanApp('Values')
    const textFieldAssetType = mapCsvTypeToFieldAssetTypeFilename[args.type]()

    const filename = sanitizeFilename(
      `${textSemios}-${textInterval}-${textValues}-${textFieldAssetType}-${args.property.propertyName}.csv`,
    )

    const content = makeCsvString({
      csvHeaderMap,
      csvRowMap,
      headerOrder: {
        'fieldAssetItem.propertyName': 1,
        'fieldAssetItem.name': 2,
        'timestamp': 3,
        'cropName': 4,
      },
      rowsOrderBy: [
        ['fieldAssetItem.name', 'asc'],
        ['timestamp', 'asc'],
      ],
    })

    if (isSemiosEmployeeOrTester()) {
      // eslint-disable-next-line no-console
      console.log({
        filename,
        csvHeaderMapEntries: Array.from(csvHeaderMap.entries()),
        csvRowMapEntries: Array.from(csvRowMap.entries()),
      })
    }

    return {
      filename: filename,
      content: content,
    }
  } else {
    return false
  }
}
